import React, { useEffect, useRef, useState } from 'react';

/**
 * PracticePlayground — extra training widgets for the QA course:
 *  1. Dialogs playground (alert / confirm / prompt) — Lesson 9
 *  2. Shadow DOM playground (open shadow root with input + button) — Lesson 10
 *  3. Tooltip on hover — for hover/wait-for-visible practice
 *
 * All elements expose stable id + data-test-id so students can practise
 * different locator strategies.
 */
const PracticePlayground = () => {
    const [confirmResult, setConfirmResult] = useState('');
    const [promptResult, setPromptResult] = useState('');
    const [shadowResult, setShadowResult] = useState('');
    const shadowHostRef = useRef(null);

    // Mount a real open Shadow DOM into the host div
    useEffect(() => {
        const host = shadowHostRef.current;
        if (!host || host.shadowRoot) return;

        const root = host.attachShadow({ mode: 'open' });
        root.innerHTML = `
            <style>
                .shadow-wrap {
                    padding: 16px;
                    border: 2px dashed #6366f1;
                    border-radius: 8px;
                    background: #eef2ff;
                    font-family: system-ui, sans-serif;
                }
                .shadow-title { font-weight: 700; margin-bottom: 8px; color: #4338ca; }
                .shadow-input {
                    padding: 8px;
                    border: 1px solid #c7d2fe;
                    border-radius: 6px;
                    width: 220px;
                    margin-right: 8px;
                }
                .shadow-btn {
                    padding: 8px 14px;
                    background: #6366f1;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                }
                .shadow-btn:hover { background: #4f46e5; }
                .shadow-out { margin-top: 10px; color: #1e293b; font-size: 14px; }
            </style>
            <div class="shadow-wrap">
                <div class="shadow-title">Inside Shadow DOM 🪄</div>
                <input
                    id="shadow-input"
                    data-test-id="shadow-input"
                    class="shadow-input"
                    placeholder="Type inside the shadow root"
                />
                <button
                    id="shadow-submit"
                    data-test-id="shadow-submit"
                    class="shadow-btn"
                >
                    Submit
                </button>
                <div
                    id="shadow-output"
                    data-test-id="shadow-output"
                    class="shadow-out"
                ></div>
            </div>
        `;

        const input = root.getElementById('shadow-input');
        const btn = root.getElementById('shadow-submit');
        const out = root.getElementById('shadow-output');

        btn.addEventListener('click', () => {
            const value = input.value || '(empty)';
            out.textContent = `Shadow value: ${value}`;
            setShadowResult(value);
        });
    }, []);

    const handleAlert = () => {
        // eslint-disable-next-line no-alert
        alert('This is an alert dialog from the playground!');
    };

    const handleConfirm = () => {
        // eslint-disable-next-line no-alert
        const ok = window.confirm('Delete this travel item?');
        setConfirmResult(ok ? 'accepted' : 'dismissed');
    };

    const handlePrompt = () => {
        // eslint-disable-next-line no-alert
        const value = window.prompt('Enter your name:', 'QA Student');
        if (value === null) {
            setPromptResult('dismissed');
        } else {
            setPromptResult(value);
        }
    };

    return (
        <div
            className="playground-section section"
            id="practice-playground"
            data-test-id="practice-playground"
            style={{ marginTop: '32px' }}
        >
            <div className="container mx-auto">
                <h3
                    className="text-2xl font-bold mb-4"
                    id="playground-title"
                    data-test-id="playground-title"
                >
                    Practice Playground
                </h3>
                <p className="text-gray-600 mb-6">
                    Extra widgets to practise dialogs, Shadow DOM, and hover tooltips.
                </p>

                {/* DIALOGS — Lesson 9 */}
                <div
                    className="dialogs-block bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-yellow-500"
                    id="dialogs-block"
                    data-test-id="dialogs-block"
                >
                    <h4 className="text-xl font-semibold mb-3">Browser Dialogs</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                        <button
                            id="alert-btn"
                            data-test-id="alert-button"
                            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                            onClick={handleAlert}
                        >
                            Show Alert
                        </button>
                        <button
                            id="confirm-btn"
                            data-test-id="confirm-button"
                            className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                            onClick={handleConfirm}
                        >
                            Show Confirm
                        </button>
                        <button
                            id="prompt-btn"
                            data-test-id="prompt-button"
                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                            onClick={handlePrompt}
                        >
                            Show Prompt
                        </button>
                    </div>
                    <div
                        className="dialog-results text-sm"
                        id="dialog-results"
                        data-test-id="dialog-results"
                    >
                        <div id="confirm-result" data-test-id="confirm-result">
                            Confirm result: <span className="font-mono">{confirmResult || '-'}</span>
                        </div>
                        <div id="prompt-result" data-test-id="prompt-result">
                            Prompt result: <span className="font-mono">{promptResult || '-'}</span>
                        </div>
                    </div>
                </div>

                {/* SHADOW DOM — Lesson 10 */}
                <div
                    className="shadow-dom-block bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-indigo-500"
                    id="shadow-dom-block"
                    data-test-id="shadow-dom-block"
                >
                    <h4 className="text-xl font-semibold mb-3">Shadow DOM Widget</h4>
                    <p className="text-sm text-gray-600 mb-3">
                        The block below has an open Shadow Root. Playwright pierces it
                        automatically — selectors look like regular ones.
                    </p>
                    <div
                        id="shadow-host"
                        data-test-id="shadow-host"
                        ref={shadowHostRef}
                    />
                    <div
                        id="shadow-host-output"
                        data-test-id="shadow-host-output"
                        className="mt-3 text-sm text-gray-700"
                    >
                        Last submitted from shadow: <span className="font-mono">{shadowResult || '-'}</span>
                    </div>
                </div>

                {/* TOOLTIP / HOVER */}
                <div
                    className="tooltip-block bg-white rounded-lg shadow p-6 mb-6 border-l-4 border-pink-500"
                    id="tooltip-block"
                    data-test-id="tooltip-block"
                >
                    <h4 className="text-xl font-semibold mb-3">Hover Tooltip</h4>
                    <div className="relative inline-block group">
                        <button
                            id="tooltip-trigger"
                            data-test-id="tooltip-trigger"
                            className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
                        >
                            Hover me
                        </button>
                        <div
                            id="tooltip-content"
                            data-test-id="tooltip-content"
                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute left-0 top-full mt-2 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow whitespace-nowrap pointer-events-none"
                        >
                            Tooltip revealed on hover
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PracticePlayground;
